// src/components/map/hooks/adapters/useSpiderifyLayer.ts
//
// Sub-hook for useFacilityHighlightAdapter.
// Manages the spiderify hover visualization that fans out overlapping
// facility icons into a radial layout, plus icon-color manipulation,
// move/zoom cleanup, and facility-icon-size tracking.
//
// The consuming adapter is responsible for composing this with other
// sub-hooks and wiring cross-cutting concerns (clearEverything, hotkeys).
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { Feature, Point, featureCollection, lineString, point } from "@turf/helpers";
import type { EventData, GeoJSONSource, PointLike } from "mapbox-gl";
import { useMapStore } from "stores/map";
import { selectDisplaySettings } from "stores/map/mapSelectors";
import { MapInteraction, MouseInteraction } from "types/maps/interactions";

import {
  FACILITY_LAYER,
  FACILITY_TYPE_ENUM,
  SPIDER_FACILITY_LAYER,
  SPIDER_FACILITY_SOURCE,
  ZOOM_LEVEL_TO_ACTIVATE_SPIDERIFY
} from "../../../../constants";
import { EntityKind } from "../../../../models/entityKind";
import type { RootState } from "../../../../store/rootReducer";
import { emptyFeatureCollection } from "../../utils/emptyFeatureCollection";
import { removeSpiderLayers, spiderifyFeatures } from "../../utils/spiderify";
import { useMapContext } from "../useMapContext";

// ── Constants ────────────────────────────────────────────────────────────────

const HIGHLIGHTED_FACILITY = "highlighted-facility";
const GASPLANT_SPIDERIFY_OFFSET = 120;

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseSpiderifyLayer {
  /** Restore the original icon-color paint property on the facility layer. */
  readonly restoreOriginalColors: () => void;
  /** Remove all spider layers and sources from the map. */
  readonly removeAllSpiderLayers: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Sub-hook that manages the spiderify hover visualization on the Mapbox map.
 *
 * Responsibilities:
 * - Detects overlapping facility icons on mouse hover
 * - Creates radial fan-out (spider) of overlapping facilities
 * - Manipulates facility-layer icon-color to hide overlapping icons
 * - Cleans up spider layers on move/zoom/entity-kind change
 * - Tracks facility icon size for spider layer sizing
 * - Invalidates stored color expression when color scheme changes
 */
export function useSpiderifyLayer(): UseSpiderifyLayer {
  // ── Map context ────────────────────────────────────────────────────

  const { mapbox, facilitiesInGroup } = useMapContext();

  // ── Store reads ────────────────────────────────────────────────────

  const displaySettings = useMapStore(selectDisplaySettings);
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );

  // ── Refs ───────────────────────────────────────────────────────────

  /** Stores the original icon-color expression before spiderify modifies it. */
  const origColorExpr = useRef<unknown>(null);

  /** Tracks the current facility icon size for spider layer icon sizing. */
  const facilityIconSizeRef = useRef(1.0);

  // ── Facility icon size tracking ────────────────────────────────────

  useEffect(() => {
    facilityIconSizeRef.current = displaySettings?.facilityIconSize || 1.0;
  }, [displaySettings?.facilityIconSize]);

  // ── Public actions ─────────────────────────────────────────────────

  const restoreOriginalColors = useCallback(() => {
    if (!mapbox || origColorExpr.current === null) return;
    mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", origColorExpr.current);
    origColorExpr.current = null;
  }, [mapbox]);

  const removeAllSpiderLayers = useCallback(() => {
    if (!mapbox) return;
    removeSpiderLayers(mapbox);
  }, [mapbox]);

  // ── Effect: hover event listeners (mouseenter/mouseleave) ──────────

  useEffect(() => {
    if (!mapbox) return;

    if (!activeEntityKinds.includes(EntityKind.Facility)) {
      return;
    }

    const clearFacilityHighlight = () => {
      if (!mapbox) return;
      if (mapbox.getLayer(HIGHLIGHTED_FACILITY)) {
        (mapbox.getSource(HIGHLIGHTED_FACILITY) as GeoJSONSource)?.setData({
          ...emptyFeatureCollection
        });
      }
    };

    const onMouseMove = (e: EventData) => {
      if (!mapbox) return;

      const zoomLevel = mapbox.getZoom();
      const facilityIconSize = displaySettings?.facilityIconSize || 1.0;

      // Scale buffer based on both zoom level and icon size
      const baseBuffer = zoomLevel * 1.5;
      const iconSizeMultiplier = facilityIconSize;
      const buffer = baseBuffer * iconSizeMultiplier;

      const bbox = [
        [e.point.x - buffer, e.point.y - buffer],
        [e.point.x + buffer, e.point.y + buffer]
      ] as [PointLike, PointLike];

      // Query both facility and spider layers
      const layerIds = [FACILITY_LAYER, SPIDER_FACILITY_LAYER].filter((id) =>
        mapbox.getLayer(id)
      );

      const allFeatures = mapbox.queryRenderedFeatures(bbox, {
        layers: layerIds
      }) as Feature<Point>[];

      const facilityFeatures = mapbox.queryRenderedFeatures(bbox, {
        layers: [FACILITY_LAYER].filter((id) => mapbox.getLayer(id))
      }) as Feature<Point>[];

      // No features found — clean up and return
      if (allFeatures.length === 0) {
        removeSpiderLayers(mapbox);
        return;
      }

      // If spider layer exists, check if we're hovering over spider facilities
      if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
        const spiderFeatures = allFeatures.filter(
          (f) => (f as mapboxgl.MapboxGeoJSONFeature).layer.id === SPIDER_FACILITY_LAYER
        );

        if (spiderFeatures.length > 0) {
          return;
        }
      }

      // Single facility — restore colors if previously modified
      if (facilityFeatures.length === 1 && !mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
        if (origColorExpr.current !== null) {
          mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", origColorExpr.current);
          origColorExpr.current = null;
        }
        return;
      }

      // Multiple facilities — create spider layer
      if (facilityFeatures.length > 1 && zoomLevel >= ZOOM_LEVEL_TO_ACTIVATE_SPIDERIFY) {
        // Don't recreate if already exists
        if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
          return;
        }

        // Store original expression BEFORE modification
        if (origColorExpr.current === null) {
          const currentExpr = mapbox.getPaintProperty(FACILITY_LAYER, "icon-color");
          origColorExpr.current = currentExpr;
        }

        // Hide overlapping icons
        const idsToHide = facilityFeatures
          .map((f) => f.properties?.Uwi)
          .filter((u): u is string => !!u);

        mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", [
          "case",
          ["in", ["get", "Uwi"], ["literal", idsToHide]],
          "rgba(0,0,0,0)",
          origColorExpr.current
        ]);

        const origin = facilityFeatures[0].geometry.coordinates as [number, number];
        const isAnyGasPlant = facilityFeatures.some(
          (f) => f.properties?.facilityType === FACILITY_TYPE_ENUM.GasPlant
        );
        const offsetPx = isAnyGasPlant ? GASPLANT_SPIDERIFY_OFFSET : 50;
        const fanOut = spiderifyFeatures(facilityFeatures, origin, mapbox, offsetPx);

        // Render spider origin point
        const originFeat = point(origin);
        mapbox.addSource("spider-origin-source", {
          type: "geojson",
          data: featureCollection([originFeat])
        });
        mapbox.addLayer({
          id: "spider-origin-layer",
          type: "circle",
          source: "spider-origin-source",
          paint: {
            "circle-radius": 6,
            "circle-color": "#000",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#fff"
          }
        });

        // Render spidered facility icons
        mapbox.addSource(SPIDER_FACILITY_SOURCE, {
          type: "geojson",
          data: featureCollection(fanOut)
        });

        const spiderColorExpr =
          origColorExpr.current !== null ? origColorExpr.current : "#FF6B6B";

        const currentFacilityIconSize = facilityIconSizeRef.current;

        mapbox.addLayer({
          id: SPIDER_FACILITY_LAYER,
          type: "symbol",
          source: SPIDER_FACILITY_SOURCE,
          layout: {
            "icon-image": [
              "match",
              ["get", "facilityType"],
              "CompressorStation",
              "compressor-station",
              FACILITY_TYPE_ENUM.GasPlant,
              "gas-plant",
              "Battery",
              "battery",
              "MeteringStation",
              "metering-station",
              "WastePlant",
              "waste-plant",
              "InjectionDisposalFacility",
              "injection-facility",
              "GatheringSystem",
              "gathering-system",
              /* default */ "default-facility"
            ],
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              6,
              0.3 * currentFacilityIconSize,
              10,
              1.1 * currentFacilityIconSize,
              12,
              1.6 * currentFacilityIconSize,
              22,
              2.1 * currentFacilityIconSize
            ],
            "icon-allow-overlap": true
          },
          paint: {
            "icon-color": spiderColorExpr
          }
        });

        // Render connecting legs
        const legs = fanOut.map((pt) => lineString([origin, pt.geometry.coordinates]));
        mapbox.addSource("spider-legs-source", {
          type: "geojson",
          data: featureCollection(legs)
        });
        mapbox.addLayer({
          id: "spider-legs-layer",
          type: "line",
          source: "spider-legs-source",
          paint: { "line-color": "#888", "line-width": 1 }
        });
      }
    };

    const hoverLayers = [FACILITY_LAYER, SPIDER_FACILITY_LAYER];

    mapbox.on(MouseInteraction.MouseEnter, hoverLayers, onMouseMove);
    mapbox.on(MouseInteraction.MouseLeave, FACILITY_LAYER, clearFacilityHighlight);

    return () => {
      hoverLayers.forEach((layer) => {
        mapbox.off(MouseInteraction.MouseEnter, layer, onMouseMove);
      });
      mapbox.off(MouseInteraction.MouseLeave, FACILITY_LAYER, clearFacilityHighlight);
    };
  }, [mapbox, displaySettings?.facilityIconSize, activeEntityKinds]);

  // ── Effect: move/zoom cleanup ──────────────────────────────────────

  useEffect(() => {
    if (!mapbox) return;

    const clear = () => {
      if (origColorExpr.current !== null) {
        mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", origColorExpr.current);
        origColorExpr.current = null;
      }
      removeSpiderLayers(mapbox);
    };

    mapbox.on(MapInteraction.MoveStart, clear);
    mapbox.on(MapInteraction.ZoomStart, clear);

    return () => {
      mapbox.off(MapInteraction.MoveStart, clear);
      mapbox.off(MapInteraction.ZoomStart, clear);
    };
  }, [mapbox]);

  // ── Effect: invalidate color expression on color scheme change ─────

  useEffect(() => {
    if (!mapbox) return;
    if (origColorExpr.current !== null) {
      origColorExpr.current = null;
    }
  }, [facilitiesInGroup, mapbox]);

  // ── Effect: update spider layer icon size ──────────────────────────

  useEffect(() => {
    if (!mapbox || !displaySettings?.facilityIconSize) return;

    if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
      const newIconSizeExpression = [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        0.3 * displaySettings.facilityIconSize,
        10,
        1.1 * displaySettings.facilityIconSize,
        12,
        1.6 * displaySettings.facilityIconSize,
        22,
        2.1 * displaySettings.facilityIconSize
      ];

      mapbox.setLayoutProperty(SPIDER_FACILITY_LAYER, "icon-size", newIconSizeExpression);
    }
  }, [mapbox, displaySettings?.facilityIconSize]);

  // ── Effect: remove spider layers when facility kind deactivated ────

  useEffect(() => {
    if (!activeEntityKinds.includes(EntityKind.Facility)) {
      if (mapbox) removeSpiderLayers(mapbox);
    }
  }, [activeEntityKinds, mapbox]);

  // ── Return ─────────────────────────────────────────────────────────

  return {
    restoreOriginalColors,
    removeAllSpiderLayers
  };
}
